"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  alpha,
  Paper,
  Alert,
  Fade,
  useTheme,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import RefreshIcon from "@mui/icons-material/Refresh"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"

const GestionMonedaComponent = () => {
  const theme = useTheme()
  const { monedas, getMoneda, postMoneda, updateMoneda, deleteMoneda, isNavbarCollapsed,
  } = useContext(ErpContext)

  const [descripcionMoneda, setDescripcionMoneda] = useState("")
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [error, setError] = useState(null)

  // Estados para edición
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [editingMoneda, setEditingMoneda] = useState(null)
  const [editDescripcion, setEditDescripcion] = useState("")

  // Estados para eliminación
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deletingMoneda, setDeletingMoneda] = useState(null)

  useEffect(() => {
    fetchMonedas()
  }, [])

  const fetchMonedas = async () => {
    setLoading(true)
    try {
      await getMoneda()
    } catch (err) {
      console.error("Error al cargar monedas:", err)
      setError("No se pudieron cargar las monedas. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleMonedaSubmit = async (e) => {
    e.preventDefault()
    if (!descripcionMoneda.trim()) {
      setError("Por favor ingrese una descripción para la moneda")
      return
    }

    setLoading(true)
    try {
      await postMoneda(descripcionMoneda)
      setDescripcionMoneda("")
      setSuccessMessage("Moneda agregada correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error("Error al agregar moneda:", err)
      setError("No se pudo agregar la moneda. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  // Funciones para edición
  const handleOpenEditDialog = (moneda) => {
    setEditingMoneda(moneda)
    setEditDescripcion(moneda.descripcion)
    setOpenEditDialog(true)
  }

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false)
    setEditingMoneda(null)
    setEditDescripcion("")
  }

  const handleUpdateMoneda = async () => {
    if (!editDescripcion.trim()) {
      setError("Por favor ingrese una descripción para la moneda")
      return
    }

    setLoading(true)
    try {
      await updateMoneda(editingMoneda.id, editDescripcion)
      setSuccessMessage("Moneda actualizada correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      handleCloseEditDialog()
    } catch (err) {
      console.error("Error al actualizar moneda:", err)
      setError("No se pudo actualizar la moneda. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  // Funciones para eliminación
  const handleOpenDeleteDialog = (moneda) => {
    setDeletingMoneda(moneda)
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
    setDeletingMoneda(null)
  }

  const handleDeleteMoneda = async () => {
    setLoading(true)
    try {
      await deleteMoneda(deletingMoneda.id)
      setSuccessMessage("Moneda eliminada correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      handleCloseDeleteDialog()
    } catch (err) {
      console.error("Error al eliminar moneda:", err)
      setError("No se pudo eliminar la moneda. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Encabezado y formulario */}
      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CurrencyExchangeIcon
              sx={{
                fontSize: 28,
                color: "rgb(244, 147, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Gestión de Monedas
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleMonedaSubmit}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <TextField
                label="Descripción de Moneda"
                variant="outlined"
                fullWidth
                value={descripcionMoneda}
                onChange={(e) => setDescripcionMoneda(e.target.value)}
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: 2,
                    minWidth: 140,
                    backgroundColor: "#5b5b5f",
                    "&:hover": {
                      backgroundColor: alpha("#5b5b5f", 0.9),
                    },
                  }}
                >
                  Agregar Moneda
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={fetchMonedas}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    minWidth: 100,
                  }}
                >
                  Actualizar
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Alertas */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setSuccess(false)}
        >
          {successMessage}
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Lista de monedas */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AttachMoneyIcon
              sx={{
                fontSize: 24,
                color: "rgb(244, 147, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Monedas Disponibles
            </Typography>
            <Chip
              label={`Total: ${monedas.length}`}
              size="small"
              sx={{ ml: 2, bgcolor: alpha("#5b5b5f", 0.1), fontWeight: 500 }}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Paper
            elevation={0}
            sx={{
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 2,
              p: 0,
            }}
          >
            <List sx={{ p: 0 }}>
              {monedas.length > 0 ? (
                monedas.map((moneda) => (
                  <ListItem
                    key={moneda.id}
                    divider
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          edge="end"
                          aria-label="editar"
                          onClick={() => handleOpenEditDialog(moneda)}
                          sx={{
                            color: "primary.main",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="eliminar"
                          onClick={() => handleOpenDeleteDialog(moneda)}
                          sx={{
                            color: "error.main",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Chip label={moneda.id} size="small" variant="outlined" sx={{ mr: 2, minWidth: 40 }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {moneda.descripcion}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography color="text.secondary" align="center">
                        No hay monedas registradas
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </CardContent>
      </Card>

      {/* Modal de edición */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <EditIcon sx={{ mr: 1, color: "primary.main" }} />
            Editar Moneda
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Descripción de Moneda"
            type="text"
            fullWidth
            variant="outlined"
            value={editDescripcion}
            onChange={(e) => setEditDescripcion(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditDialog} variant="outlined" disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdateMoneda}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#5b5b5f",
              "&:hover": {
                backgroundColor: alpha("#5b5b5f", 0.9),
              },
            }}
          >
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", color: "error.main" }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Confirmar Eliminación
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar la moneda{" "}
            <strong>{deletingMoneda?.descripcion}</strong>?
            <br />
            <br />
            Esta acción marcará la moneda como eliminada.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} variant="outlined" disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteMoneda}
            variant="contained"
            color="error"
            disabled={loading}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default GestionMonedaComponent

